import { useParams } from "react-router-dom";
import { ObjectMap } from "../framework/entities/object-map";
import { forEach } from "../framework/utils/collection";
import { PageAdminSignIn } from "./auth/pages/sign-in";
import { PageImportedLowesStormDoors } from "./lowes-storm-door/pages/Imported-doors";
import { PagePublishLowesStormDoor } from "./lowes-storm-door/pages/publish-door";
import { PageImportedLowesStormHandles } from "./lowes-storm-handle/pages/imported-handles";
import { PagePublishLowesStormHandle } from "./lowes-storm-handle/pages/publish-handle";
import { PageOpenProjects } from "./project/pages/open-projects";
import { PageEditStormDoor } from "./storm-door/pages/edit-door";
import { PageViewStormDoors } from "./storm-door/pages/view-doors";
import { PageEditStormHandle } from "./storm-handle/pages/edit-handle";
import { PageViewStormHandles } from "./storm-handle/pages/view-handles";
import { PageManageTimeBlocks } from "./time-block/pages/manage-blocks";
import { PageRecordCompletedAssessment } from "./project/pages/record-assessment";
import { IAdminAppRoute, IAdminAppRouteBase } from "./admin-app-route";
import { PageProjectOverview } from "./project/pages/project-overview";
import { PageProjectOverviewGeneralInfo } from "./project/pages/project-overview/general-info";
import { PageProjectOverviewMaterials } from "./project/pages/project-overview/materials";
import { PageProjectOverviewInstallationSiteDoor } from "./project/pages/project-overview/install-site-door";
import { PageProjectOverviewInstallationSite } from "./project/pages/project-overview/install-site";
import { PageProjectOverviewInstallationSitePhotos } from "./project/pages/project-overview/install-site-photos";
import { PageManagePurchaseIncentives } from "./project/pages/purchase-incentives";

// use to build a regex that will match to a URL path without a bunch of escaping:
// e.g. /customers/:guid
// note that the URL starts with a slash.
// Placeholders are what make this useful. Use :guid to refer to a guid id (no dashes), and :int to refer to a whole number identifier
const buildRegex = (template: string) => {
    let normalizedTemplate = template.trim();
    normalizedTemplate = normalizedTemplate[0] === '/' ? normalizedTemplate.substring(1) : normalizedTemplate;

    const pathParts = normalizedTemplate.split('/');
    const actualParts = pathParts.map(p => {
        if(p === ':guid') {
            return '([0-9a-f]{32})';
        }
        if(p === ':int') {
            return '([0-9]+)';
        }
        if(p === ':any') {
            return '([a-z0-9-]+)';
        }
        return p;
    });

    const rx = `^\\/${actualParts.join('\\/')}$`;
    return new RegExp(rx, 'i');
};

class AppRouteConfig {
    public home: IAdminAppRoute = {
        key: 'home',
        urlTemplate: '/',
        url: () => '/',
        icon: 'Home',
        exact: true,
        regex: null,
        allowAnonymous: false,
        sideMenuLabel: undefined,
        sideMenuSupported: true,
        topLevel: true,
        component: PageOpenProjects,
    };

    public signIn: IAdminAppRoute = {
        key: 'sign-in',
        urlTemplate: '/sign-in',
        url: () => '/sign-in',
        icon: 'AzureKeyVault',
        exact: true,
        regex: null,
        allowAnonymous: true,
        sideMenuLabel: undefined,
        sideMenuSupported: false,
        topLevel: true,
        component: PageAdminSignIn,
    };

    public importedLowesStormDoors: IAdminAppRoute = {
        key: 'imported-lowes-storm-doors',
        urlTemplate: '/vendors/lowes/doors/storm',
        url: () => '/vendors/lowes/doors/storm',
        icon: 'PageHeader',
        exact: true,
        regex: null,
        allowAnonymous: false,
        sideMenuLabel: 'Lowe\'s Storm Doors',
        sideMenuSupported: true,
        topLevel: true,
        component: PageImportedLowesStormDoors,
    };

    public publishLowesStormDoor: IAdminAppRoute<(id: string) => string> = {
        key: 'publish-lowes-storm-door',
        urlTemplate: '/vendors/lowes/doors/storm/:id',
        url: id => `/vendors/lowes/doors/storm/${id}`,
        icon: 'PageHeader',
        exact: true,
        regex: buildRegex('/vendors/lowes/doors/storm/:any'),
        allowAnonymous: false,
        sideMenuLabel: undefined,
        sideMenuSupported: true,
        topLevel: true,
        component: PagePublishLowesStormDoor,
    };

    public importedLowesStormHandles: IAdminAppRoute = {
        key: 'imported-lowes-storm-handles',
        urlTemplate: '/vendors/lowes/handles/storm',
        url: () => '/vendors/lowes/handles/storm',
        icon: 'HorizontalDistributeCenter',
        exact: true,
        regex: null,
        allowAnonymous: false,
        sideMenuLabel: 'Lowe\'s St. Dr. Handles',
        sideMenuSupported: true,
        topLevel: true,
        component: PageImportedLowesStormHandles,
    };

    public publishLowesStormHandle: IAdminAppRoute<(id: string) => string> = {
        key: 'publish-lowes-storm-handle',
        urlTemplate: '/vendors/lowes/handles/storm/:id',
        url: id => `/vendors/lowes/handles/storm/${id}`,
        icon: 'HorizontalDistributeCenter',
        exact: true,
        regex: buildRegex('/vendors/lowes/handles/storm/:any'),
        allowAnonymous: false,
        sideMenuLabel: undefined,
        sideMenuSupported: true,
        topLevel: true,
        component: PagePublishLowesStormHandle,
    };

    public viewStormDoors: IAdminAppRoute = {
        key: 'view-storm-doors',
        urlTemplate: '/doors/storm',
        url: () => '/doors/storm',
        icon: 'PageHeader',
        exact: true,
        regex: null,
        allowAnonymous: false,
        sideMenuLabel: 'Storm Door Inventory',
        sideMenuSupported: true,
        topLevel: true,
        component: PageViewStormDoors,
    };

    public editStormDoor: IAdminAppRoute<(id: string) => string> = {
        key: 'edit-storm-door',
        urlTemplate: '/doors/storm/:id',
        url: id => `/doors/storm/${id}`,
        icon: 'PageHeader',
        exact: true,
        regex: buildRegex('/doors/storm/:guid'),
        allowAnonymous: false,
        sideMenuLabel: undefined,
        sideMenuSupported: true,
        topLevel: true,
        component: PageEditStormDoor,
    };

    public viewStormHandles: IAdminAppRoute = {
        key: 'view-storm-handles',
        urlTemplate: '/handles/storm',
        url: () => '/handles/storm',
        icon: 'HorizontalDistributeCenter',
        exact: true,
        regex: null,
        allowAnonymous: false,
        sideMenuLabel: 'Stm. Handle Inventory',
        sideMenuSupported: true,
        topLevel: true,
        component: PageViewStormHandles,
    };

    public editStormHandle: IAdminAppRoute<(id: string) => string> = {
        key: 'edit-storm-handle',
        urlTemplate: '/handles/storm/:id',
        url: id => `/handles/storm/${id}`,
        icon: 'HorizontalDistributeCenter',
        exact: true,
        regex: buildRegex('/handles/storm/:guid'),
        allowAnonymous: false,
        sideMenuLabel: undefined,
        sideMenuSupported: true,
        topLevel: true,
        component: PageEditStormHandle,
    };

    public manageTimeBlocks: IAdminAppRoute = {
        key: 'manage-time-blocks',
        urlTemplate: '/time-blocks',
        url: () => '/time-blocks',
        icon: 'DateTime',
        exact: true,
        regex: null,
        allowAnonymous: false,
        sideMenuLabel: 'Time Blocks',
        sideMenuSupported: true,
        topLevel: true,
        component: PageManageTimeBlocks,
    };

    public openProjects: IAdminAppRoute = {
        key: 'open-projects',
        urlTemplate: '/projects/open',
        url: () => '/projects/open',
        icon: 'OpenFolderHorizontal',
        exact: true,
        regex: null,
        allowAnonymous: false,
        sideMenuLabel: 'Open Projects',
        sideMenuSupported: true,
        topLevel: true,
        component: PageOpenProjects,
    };

    public recordCompletedAssessment: IAdminAppRoute<(id: string) => string> = {
        key: 'record-completed-assessment',
        urlTemplate: '/projects/:id/assessment/document',
        url: id => `/projects/${id}/assessment/document`,
        icon: 'ClipboardList',
        exact: true,
        regex: buildRegex('/projects/:any/assessment/document'),
        allowAnonymous: false,
        sideMenuLabel: undefined,
        sideMenuSupported: true,
        topLevel: true,
        component: PageRecordCompletedAssessment,
    };

    public managePurchaseIncentives: IAdminAppRoute<(id: string) => string> = {
        key: 'manage-purchase-incentives',
        urlTemplate: '/projects/:id/incentives',
        url: id => `/projects/${id}/incentives`,
        icon: 'Money',
        exact: true,
        regex: buildRegex('/projects/:any/incentives'),
        allowAnonymous: false,
        sideMenuLabel: undefined,
        sideMenuSupported: true,
        topLevel: true,
        component: PageManagePurchaseIncentives,
    };

    public projectOverview: IAdminAppRoute<(id: string) => string> = {
        key: 'project-overview',
        urlTemplate: '/projects/:id/overview',
        url: id => `/projects/${id}/overview`,
        icon: 'ClipboardList',
        exact: false,
        regex: buildRegex('/projects/:any/overview'),
        allowAnonymous: false,
        sideMenuLabel: undefined,
        sideMenuSupported: true,
        topLevel: true,
        component: PageProjectOverview,
    };

    public projectOverviewAbout: IAdminAppRoute<(id: string) => string> = {
        key: 'project-overview-about',
        urlTemplate: '/projects/:id/overview/about',
        url: id => `/projects/${id}/overview/about`,
        icon: 'ContactInfo',
        exact: true,
        regex: buildRegex('/projects/:any/overview/about'),
        allowAnonymous: false,
        sideMenuLabel: 'About',
        sideMenuSupported: true,
        topLevel: false,
        component: PageProjectOverviewGeneralInfo,
    };

    public projectOverviewMaterials: IAdminAppRoute<(id: string) => string> = {
        key: 'project-overview-materials',
        urlTemplate: '/projects/:id/overview/materials',
        url: id => `/projects/${id}/overview/materials`,
        icon: 'WebAppBuilderFragment',
        exact: true,
        regex: buildRegex('/projects/:any/overview/materials'),
        allowAnonymous: false,
        sideMenuLabel: 'Materials',
        sideMenuSupported: true,
        topLevel: false,
        component: PageProjectOverviewMaterials,
    };

    public projectOverviewInstallSite: IAdminAppRoute<(id: string, site: string) => string> = {
        key: 'project-overview-install-site',
        urlTemplate: '/projects/:id/overview/:site',
        url: (id, site) => `/projects/${id}/overview/${site}`,
        icon: 'Home',
        exact: false,
        regex: buildRegex('/projects/:any/overview/:any'),
        allowAnonymous: false,
        sideMenuLabel: undefined,
        sideMenuSupported: true,
        topLevel: false,
        component: PageProjectOverviewInstallationSite,
    };

    public projectOverviewInstallSitePhotos: IAdminAppRoute<(id: string, site: string) => string> = {
        key: 'project-overview-install-site-photos',
        urlTemplate: '/projects/:id/overview/:site/photos',
        url: (id, site) => `/projects/${id}/overview/${site}/photos`,
        icon: 'Photo2',
        exact: true,
        regex: buildRegex('/projects/:any/overview/:any/photos'),
        allowAnonymous: false,
        sideMenuLabel: 'Photos',
        sideMenuSupported: true,
        topLevel: false,
        component: PageProjectOverviewInstallationSitePhotos,
    };

    public projectOverviewInstallSiteDoor: IAdminAppRoute<(id: string, site: string, door: string) => string> = {
        key: 'project-overview-install-site-door',
        urlTemplate: '/projects/:id/overview/:site/:door',
        url: (id, site, door) => `/projects/${id}/overview/${site}/${door}`,
        icon: 'Door',
        exact: true,
        regex: buildRegex('/projects/:any/overview/:any/:any'),
        allowAnonymous: false,
        sideMenuLabel: undefined,
        sideMenuSupported: true,
        topLevel: false,
        component: PageProjectOverviewInstallationSiteDoor,
    };
}

export const AppRoutes = new AppRouteConfig();

export function getAppRouteByKey(key: string): IAdminAppRouteBase | undefined {
    const appRoutes = AppRoutes as unknown as ObjectMap<IAdminAppRouteBase>;
    let selectedRoute: IAdminAppRouteBase | undefined = undefined;

    forEach(appRoutes, route => {
        if(route.key === key) {
            selectedRoute = route;
            return false; // break
        }
    });

    return selectedRoute;
}

export const getAppRouteByPathname = (pathname: string): IAdminAppRouteBase | undefined => {
    const appRoutes = AppRoutes as unknown as ObjectMap<IAdminAppRouteBase>;
    let matchingRoute: IAdminAppRouteBase | undefined = undefined;

    forEach(appRoutes, route => {
        if((!!route.regex && route.regex.test(pathname)) ||
        (!route.regex && route.urlTemplate === pathname.toLowerCase())) {
            matchingRoute = route;
            return false; // break;
        }
    });

    return matchingRoute;
}

export const getAppRoutesArray = (): IAdminAppRouteBase[] => {
    const appRoutes = AppRoutes as unknown as ObjectMap<IAdminAppRouteBase>;
    const routes: IAdminAppRouteBase[] = [];

    forEach(appRoutes, route => {
        routes.push(route);
    });

    return routes;
};

export const toProjectSlug = (projectId: string) => projectId.replace('.', '');
export const useProjectId = () => {
    const { id } = useParams<{id: string}>();
    return `${id.substring(0, 4)}.${id.substring(4)}`;
}
export const useProjectOverviewParams = () => {
    const { id, site, door } = useParams<{ id: string, site: string | undefined, door: string | undefined }>();
    return { id: `${id.substring(0, 4)}.${id.substring(4)}`, site, door };
}